import React, { useState, useRef, useEffect } from "react";
import { strings } from "../../strings/strings";

const Select = ({
  label,
  required = false,
  error,
  touched,
  className = "",
  options = [],
  placeholder = "Select an option",
  value,
  onChange,
  onBlur,
  name,
  dropdownDirection = "down",
  onMenuScroll,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const hasError = touched && error;
  const selectId = props.id || name || `select-${Math.random().toString(36).substr(2, 9)}`;

  const handleScroll = (e) => {
    if (!onMenuScroll) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      onMenuScroll();
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    if (onChange) {
      const event = {
        target: {
          name: name,
          value: optionValue,
        },
      };
      onChange(event);
    }
    setIsOpen(false);
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur({
        target: {
          name: name,
        },
      });
    }
  };

  const selectedOption = options.find((opt) => {
    if (typeof opt === "string") {
      return opt === value;
    }
    return opt.value === value;
  });

  const displayValue = selectedOption
    ? typeof selectedOption === "string"
      ? selectedOption
      : selectedOption.label || selectedOption.value
    : value || placeholder;

  return (
    <div className="w-full" ref={selectRef}>
      {label && (
        <label htmlFor={selectId} className="block text-sm font-medium text-secondary mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          id={selectId}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          onBlur={handleBlur}
          className={`w-full px-4 py-3 rounded-lg text-sm text-secondary placeholder-gray-400 
            focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary transition-colors 
            ${hasError ? "border-red-300 bg-red-50" : "border-gray-300 bg-grey"} 
            ${className}
            flex items-center justify-between
            ${!value ? "text-gray-400" : "text-secondary"}`}
        >
          <span className="truncate">{displayValue}</span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && options.length > 0 && (
          <div
            className={`absolute z-[9999] w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto ${
              dropdownDirection === "up" ? "bottom-full mb-1" : "mt-1"
            }`}
            onClick={(e) => e.stopPropagation()}
            onScroll={handleScroll}
          >
            {options.map((option, index) => {
              const optionValue = typeof option === "string" ? option : option.value;
              const optionLabel = typeof option === "string" ? option : option.label || option.value;
              const isSelected = value === optionValue;

              return (
                <button
                  key={index}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(optionValue);
                  }}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors hover:bg-gray-50 
                    ${isSelected ? "bg-primary/10 text-primary font-medium" : "text-secondary"}
                    ${index === 0 ? "rounded-t-lg" : ""}
                    ${index === options.length - 1 ? "rounded-b-lg" : ""}`}
                >
                  {optionLabel}
                </button>
              );
            })}
          </div>
        )}

        {isOpen && options.length === 0 && (
          <div
            className={`absolute z-[9999] w-full bg-white border border-gray-300 rounded-lg shadow-lg ${
              dropdownDirection === "up" ? "bottom-full mb-1" : "mt-1"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 text-sm text-gray-500">{strings.nooptionsavailable}</div>
          </div>
        )}
      </div>

      {hasError && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default Select;
